/* eslint-disable no-var */
import React from 'react';
import { Stack, Inline, Text, useViewport } from '@treatwell/ui';
import { Direction } from 'js/components/Icons/Chevron/Chevron';
import { useWindowSize } from 'js/hooks/useWindowSize';
import { CmsOurWork } from 'js/model/cms/cms-venue-page';
import { RequestData } from 'js/helpers/service';
import { VenueOutput } from 'js/model/rainbow/venue/VenueOutput';
import { ImageOutput } from 'js/model/rainbow/ImageOutput';
import { isElementInView } from 'js/helpers/isElementInView';
import { GalleryImage } from './GalleryImage/GalleryImage';
import { GalleryNavigationItem } from './GalleryNavigationItem/GalleryNavigationItem';
import { containerWidth as carouselItemWidth } from './GalleryImage/GalleryImage.module.css';
import { containerWidth as navigationItemWidth } from './GalleryNavigationItem/GalleryNavigationItem.module.css';
import styles from './Gallery.module.css';

interface Props {
  cms: CmsOurWork;
  pageData: RequestData & { venue?: { venue: VenueOutput } };
  images: ImageOutput[];
  selectedImage: ImageOutput;
  onImageClick: (imageId: number) => void;
}

export function Gallery(props: Props): React.ReactElement {
  var NAVIGATION_ITEM_WIDTH = Number.parseInt(navigationItemWidth, 10);
  const { cms, pageData, images, selectedImage, onImageClick } = props;
  const windowSize = useWindowSize();
  const isMobile = useViewport({ device: 'mobile' });
  const isTablet = useViewport({ device: 'tablet' });
  const carouselRef = React.useRef<HTMLElement>(null);
  const selectedGalleryImageRef = React.useRef<HTMLDivElement | null>(null);
  const CAROUSEL_ITEM_WIDTH = Number.parseInt(carouselItemWidth, 10);
  const CAROUSEL_ITEM_SPACING = isTablet ? 24 : 32;
  const CONTAINER_HORIZONTAL_PADDING = isTablet
    ? Number.parseInt(styles.containerHorizontalPaddingTablet, 10)
    : Number.parseInt(styles.containerHorizontalPaddingDesktop, 10);
  const CAROUSEL_HORIZONTAL_MARGIN = isTablet
    ? Number.parseInt(styles.carouselHorizontalMarginTablet, 10)
    : Number.parseInt(styles.carouselHorizontalMarginDesktop, 10);
  const getCarouselWidth = (): string | number => {
    if (isMobile || !windowSize.width) {
      return '100%';
    }
    const MAX_CAROUSEL_ITEMS_WIDTH =
      windowSize.width -
      CONTAINER_HORIZONTAL_PADDING * 2 -
      NAVIGATION_ITEM_WIDTH * 2 -
      CAROUSEL_HORIZONTAL_MARGIN * 2;

    const pageSize = Math.floor(
      MAX_CAROUSEL_ITEMS_WIDTH / (CAROUSEL_ITEM_WIDTH + CAROUSEL_ITEM_SPACING)
    );

    return (
      (CAROUSEL_ITEM_WIDTH + CAROUSEL_ITEM_SPACING) * pageSize -
      CAROUSEL_ITEM_SPACING
    );
  };
  const [carouselWidth, setCarouselWidth] = React.useState<string | number>(
    getCarouselWidth
  );
  const [hasReachedLeftEdge, setHasReachedLeftEdge] =
    React.useState<boolean>(false);
  const [hasReachedRightEdge, setHasReachedRightEdge] =
    React.useState<boolean>(false);

  React.useLayoutEffect(() => {
    scrollSelectedGalleryImageIntoView();
  }, []);

  React.useLayoutEffect(() => {
    function onScroll(): void {
      requestAnimationFrame(updateCarouselScrollPositionStatuses);
    }

    carouselRef.current?.addEventListener('scroll', onScroll);

    onScroll();

    return () => {
      carouselRef.current?.removeEventListener('scroll', onScroll);
    };
  }, []);

  React.useLayoutEffect(() => {
    setCarouselWidth(getCarouselWidth());
    updateCarouselScrollPositionStatuses();
  }, [windowSize.width]);

  React.useLayoutEffect(() => {
    scrollSelectedGalleryImageIntoView();
  }, [carouselWidth]);

  React.useLayoutEffect(() => {
    if (
      selectedGalleryImageRef.current &&
      carouselRef.current &&
      !isElementInView(selectedGalleryImageRef.current, carouselRef.current)
    ) {
      scrollSelectedGalleryImageIntoView();
    }
  }, [selectedImage]);

  function updateCarouselScrollPositionStatuses(): void {
    const carouselElement = carouselRef.current;
    if (carouselElement) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselElement;
      const maxScrollLeft = scrollWidth - clientWidth;
      setHasReachedLeftEdge(scrollLeft === 0);
      setHasReachedRightEdge(scrollLeft === maxScrollLeft);
    }
  }

  function scrollSelectedGalleryImageIntoView(keepInViewOnly = false): void {
    if (selectedGalleryImageRef.current && carouselRef.current) {
      if (
        keepInViewOnly &&
        isElementInView(selectedGalleryImageRef.current, carouselRef.current)
      ) {
        return;
      }
      selectedGalleryImageRef.current.scrollIntoView({ inline: 'start' });
    }
  }

  function navigate(direction: 'left' | 'right'): void {
    const carouselElement = carouselRef.current;

    if (!carouselElement) {
      return;
    }

    const toLeft =
      carouselElement.scrollLeft -
      Number(carouselWidth) -
      CAROUSEL_ITEM_SPACING +
      CAROUSEL_ITEM_WIDTH +
      CAROUSEL_ITEM_SPACING;
    const toRight =
      carouselElement.scrollLeft +
      Number(carouselWidth) +
      CAROUSEL_ITEM_SPACING -
      CAROUSEL_ITEM_WIDTH -
      CAROUSEL_ITEM_SPACING;

    carouselElement.scroll({
      left: direction === 'left' ? toLeft : toRight,
      behavior: 'smooth',
    });
  }

  return (
    <Stack className={styles.container} space="xs">
      <Inline className={styles.title} justify="center">
        <Text as="div" type="smHeader" style={{ width: carouselWidth }}>
          {cms['more-from']} {pageData.venue?.venue.name}
        </Text>
      </Inline>
      <Inline justify="between">
        <GalleryNavigationItem
          isHidden={isMobile}
          isDisabled={hasReachedLeftEdge}
          chevronDirection={Direction.Left}
          onClick={() => navigate('left')}
        />
        <Inline
          forwardRef={carouselRef}
          className={styles.carousel}
          // eslint-disable-next-line no-nested-ternary
          space={isMobile ? undefined : isTablet ? 'lg' : 'xl'}
          style={{ width: carouselWidth }}
        >
          {images.map((image) => (
            <GalleryImage
              ref={(node) => {
                if (image.id === selectedImage.id) {
                  selectedGalleryImageRef.current = node;
                }
              }}
              key={image.id}
              imageUrl={image.uris['128x170']}
              isSelected={image.id === selectedImage.id}
              onClick={() => {
                onImageClick(image.id);
              }}
            />
          ))}
        </Inline>
        <GalleryNavigationItem
          isHidden={isMobile}
          isDisabled={hasReachedRightEdge}
          chevronDirection={Direction.Right}
          onClick={() => navigate('right')}
        />
      </Inline>
    </Stack>
  );
}
